"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslations } from "next-intl";
import { AnswerValue, isEmptyAnswer } from "@/commonTypes/answer";
import { Question, QuestionType, TextInputType } from "@/commonTypes/question";
import { isAnswerValid } from "@/utils/validation";
import { isConditionMet } from "@/utils/conditionEval";
import { sessionStorageExpiring, sessionItems } from "@/utils/sessionStorage";
import {
  fireQuizStarted,
  fireAnswerEvent,
  fireBackEvent,
  fireExitEvent,
  fireQuizComplete,
  formatExperimentVariants,
} from "@/utils/analytics";
import { assignAllExperiments } from "@/utils/experimentUtils";
import { applyQuestionOverrides } from "@/utils/questionOverrides";
import { getMarketingConsent } from "@/utils/marketingConsent";
import type { AssessmentResult, QuestionOverride } from "@/api/types";
import { CaptchaVerificationRequiredError } from "@/api/errors";
import { useSessionId } from "@/contexts/SessionContext/SessionContext";
import {
  QUESTIONNAIRE_ID,
  SURVEY_VERSION,
  SURVEY_VARIANT,
  API_CLIENT,
} from "@/constants/surveyMetadata";
import { Variant, PAGE_VARIANT } from "@/constants/variant";
import { useFriendlyCaptcha } from "@/contexts/FriendlyCaptchaContext/FriendlyCaptchaContext";
import type {
  IAssessmentContext,
  AssessmentProviderProps,
} from "./AssessmentContext.types";
import { PageState } from "./AssessmentContext.types";

/** Question types that are temporarily hidden from the user flow */
const HIDDEN_QUESTION_TYPES: QuestionType[] = [
  QuestionType.SELFIE_INTRO,
  QuestionType.SELFIE,
];

const AssessmentContext = createContext<IAssessmentContext | null>(null);

async function fireStartedEvent(
  api: AssessmentProviderProps["api"],
  sessionId: string,
  answers: Record<string, AnswerValue>,
  experimentAssignments: Record<string, string> | null,
) {
  await api.postResponse({
    id: sessionId,
    surveyId: QUESTIONNAIRE_ID,
    surveyVersion: SURVEY_VERSION,
    surveyVariant: SURVEY_VARIANT,
    apiClient: API_CLIENT,
    event: "started",
    answers: [
      { questionId: "user-name", answerId: answers["user-name"] },
      { questionId: "email", answerId: answers["email"] },
    ],
    ...(experimentAssignments && { experimentAssignments }),
    marketingConsent: getMarketingConsent(),
  });
}

enum FriendlyCaptchaVerificationStatus {
  Idle = "idle",
  Verifying = "verifying",
  Verified = "verified",
  Failed = "failed",
}

function isCaptchaVerificationRequiredError(error: unknown): boolean {
  return error instanceof CaptchaVerificationRequiredError;
}

function hasPersistedCaptchaVerificationForSession(sessionId: string): boolean {
  const verifiedSessionId = sessionStorageExpiring.get<string>(
    sessionItems.FRIENDLY_CAPTCHA_VERIFIED_SESSION_ID,
  );
  return verifiedSessionId === sessionId;
}

function markCaptchaVerifiedForSession(sessionId: string): void {
  sessionStorageExpiring.set(
    sessionItems.FRIENDLY_CAPTCHA_VERIFIED_SESSION_ID,
    sessionId,
  );
}

export function AssessmentProvider({
  children,
  api,
  questions: initialQuestions,
  onComplete,
}: AssessmentProviderProps) {
  const t = useTranslations();
  const { sessionId } = useSessionId();
  const friendlyCaptcha = useFriendlyCaptcha();
  const [questions, setQuestionsState] = useState<Question[]>(
    () =>
      sessionStorageExpiring.get<Question[]>(sessionItems.QUESTIONS) ??
      (initialQuestions || []),
  );
  const setQuestions = useCallback((value: Question[]) => {
    setQuestionsState(value);
    sessionStorageExpiring.set(sessionItems.QUESTIONS, value);
  }, []);

  const [answers, setAnswersState] = useState<Record<string, AnswerValue>>(
    () =>
      sessionStorageExpiring.get<Record<string, AnswerValue>>(
        sessionItems.QUIZ_ANSWERS,
      ) ?? {},
  );
  const answersRef = useRef(answers);
  answersRef.current = answers;
  // Kept in sync with visibleQuestions so goNext can read it without adding
  // visibleQuestions to its dependency array.
  const visibleQuestionsRef = useRef<Question[]>([]);
  const questionsRef = useRef<Question[]>([]); // kept in sync for exit handler closure
  const quizActiveRef = useRef(false); // true after 'start', false after 'finish'
  const currentIndexRef = useRef(0);
  const [validationState, setValidationState] = useState<
    Record<string, boolean>
  >({});
  const [currentIndex, setCurrentIndexState] = useState<number>(
    () => sessionStorageExpiring.get<number>(sessionItems.CURRENT_INDEX) ?? 0,
  );
  currentIndexRef.current = currentIndex;
  const setCurrentIndex = useCallback((value: number) => {
    setCurrentIndexState(value);
    sessionStorageExpiring.set(sessionItems.CURRENT_INDEX, value);
  }, []);

  const [isLoading, setIsLoading] = useState(
    !initialQuestions && questions.length === 0,
  );
  const [error, setError] = useState<string | null>(null);
  const [, setFriendlyCaptchaVerificationStatus] =
    useState<FriendlyCaptchaVerificationStatus>(
      friendlyCaptcha.enabled ? (friendlyCaptcha.solution ? FriendlyCaptchaVerificationStatus.Verifying : FriendlyCaptchaVerificationStatus.Idle) : FriendlyCaptchaVerificationStatus.Verified,
    );
  const [assessmentResult, setAssessmentResult] =
    useState<AssessmentResult | null>(
      () =>
        sessionStorageExpiring.get<AssessmentResult>(
          sessionItems.ASSESSMENT_RESULT,
        ) ?? null,
    );
  const [inferenceId, setInferenceId] = useState<string | null>(
    () => sessionStorageExpiring.get<string>(sessionItems.INFERENCE_ID) ?? null,
  );
  const [experimentAssignments, setExperimentAssignmentsState] =
    useState<Record<string, string> | null>(
      () =>
        sessionStorageExpiring.get<Record<string, string>>(
          sessionItems.EXPERIMENT_ASSIGNMENTS,
        ) ?? null,
    );
  const setExperimentAssignments = useCallback(
    (value: Record<string, string>) => {
      setExperimentAssignmentsState(value);
      sessionStorageExpiring.set(sessionItems.EXPERIMENT_ASSIGNMENTS, value);
    },
    [],
  );
  const [pageState, setPageStateState] = useState<PageState>(
    () =>
      sessionStorageExpiring.get<PageState>(sessionItems.PAGE_STATE) ??
      PageState.QUESTIONNAIRE,
  );
  const setPageState = useCallback((value: PageState) => {
    setPageStateState(value);
    sessionStorageExpiring.set(sessionItems.PAGE_STATE, value);
  }, []);
  const terminateSessionWithError = useCallback(() => {
    // Drop any recoverable quiz state and switch to a terminal error view.
    // This forces a fresh session on next interaction, which is appropriate
    // for security-critical failures like captcha verification.
    if (process.env.NODE_ENV !== "production") {
      console.warn("Terminating session with error state");
    }
    sessionStorageExpiring.clear();
    setPageStateState(PageState.ERROR);
    window.history.replaceState(
      {
        sapQuestionIndex: currentIndexRef.current,
        sapPageState: PageState.ERROR,
      },
      "",
    );
    quizActiveRef.current = false;
  }, []);
  const startedFiredRef = useRef(false);
  const [isQuizStarted, setIsQuizStarted] = useState(false);

  // Fire sleep_quiz_exit when user hides/closes the page mid-quiz.
  // visibilitychange is more reliable than beforeunload on mobile.
  // Only fires while the quiz is active (after 'start', before 'finish').
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return;
      if (!quizActiveRef.current) return;

      const pageVariant =
        PAGE_VARIANT[
          answersRef.current["sex-at-birth"] === "female"
            ? Variant.FEMALE
            : Variant.DEFAULT
        ];
      fireExitEvent(
        questionsRef.current[currentIndexRef.current],
        visibleQuestionsRef.current,
        pageVariant,
      );
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs, no reactive deps needed
  }, []);

  // Fetch questions if not provided and not already in session storage
  useEffect(() => {
    if (initialQuestions || questions.length > 0) return;

    const fetchQuestionnaire = async () => {
      try {
        setIsLoading(true);
        const { questions: fetchedQuestions, experiments } =
          await api.getQuestions(QUESTIONNAIRE_ID);

        // Assign user to experiment variants based on session ID and experiment ID
        const assignments = await assignAllExperiments(sessionId, experiments);
        setExperimentAssignments(assignments);

        // Collect question overrides from assigned treatment variants
        const overrides: QuestionOverride[] = [];
        for (const experiment of experiments) {
          const experimentVariantName = assignments[experiment.experimentId];

          // Continue to the next experiment if we are in the control group
          if (experimentVariantName === "control") {
            continue;
          }

          // Add the corresponding question overrides to the list of required overrides
          const experimentVariant = experiment.variants[experimentVariantName];
          if (experimentVariant?.questionOverrides) {
            overrides.push(...experimentVariant.questionOverrides);
          }
        }

        // Apply overrides if any, store fetched questions otherwise
        const processedQuestions =
          overrides.length > 0
            ? applyQuestionOverrides(fetchedQuestions, overrides)
            : fetchedQuestions;
        setQuestions(processedQuestions);
        setError(null);
      } catch (error) {
        console.error("Failed to fetch questionnaire:", error);
        setError(t("errors.load-questionnaire"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestionnaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally runs only when initialQuestions or api changes. Other refs (questions.length, t, setters) are either guards or stable values.
  }, [initialQuestions, api]);

  useEffect(() => {
    let cancelled = false;

    if (!friendlyCaptcha.enabled) {
      setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Verified);
      return;
    }

    // SessionContext may expose an empty value before hydration completes.
    if (!sessionId) {
      return;
    }

    if (hasPersistedCaptchaVerificationForSession(sessionId)) {
      setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Verified);
      return;
    }

    if (!friendlyCaptcha.solution) {
      // Missing token on refresh should not force a terminal error view.
      // Backend verification still enforces security when protected submissions occur.
      if (process.env.NODE_ENV !== "production") {
        console.debug("Captcha verification: no solution available on mount, deferring to backend enforcement");
      }
      setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Idle);
      return;
    }

    setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Verifying);
    api
      .verifyFriendlyCaptcha({
        id: sessionId,
        friendlyCaptchaSolution: friendlyCaptcha.solution,
        marketingConsent: getMarketingConsent(),
      })
      .then((verified) => {
        if (cancelled) return;
        if (!verified) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("Captcha verification failed: backend rejected solution", { sessionId });
          }
          setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Failed);
          terminateSessionWithError();
          return;
        }
        markCaptchaVerifiedForSession(sessionId);
        setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Verified);
      })
      .catch((error) => {
        if (cancelled) return;
        if (process.env.NODE_ENV !== "production") {
          console.warn("Captcha verification error", { sessionId, error });
        }
        setFriendlyCaptchaVerificationStatus(FriendlyCaptchaVerificationStatus.Failed);
        terminateSessionWithError();
      });

    return () => {
      cancelled = true;
    };
  }, [
    api,
    sessionId,
    friendlyCaptcha.enabled,
    friendlyCaptcha.solution,
    terminateSessionWithError,
  ]);

  // Browser history: tag the current entry on mount so back/forward works
  useEffect(() => {
    const existingHistoryState =
      typeof window.history.state === "object" && window.history.state !== null
        ? window.history.state
        : {};

    window.history.replaceState(
      {
        ...existingHistoryState,
        sapQuestionIndex: currentIndex,
        sapPageState: pageState,
      },
      "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only on mount
  }, []);

  // Browser history: listen for back/forward button
  useEffect(() => {
    const handlePopstate = (event: PopStateEvent) => {
      const state = event.state;
      if (!state || typeof state.sapQuestionIndex !== "number") return;

      const restoredPage = state.sapPageState as PageState | undefined;
      const restoredIndex = state.sapQuestionIndex as number;

      if (restoredPage) {
        setPageState(restoredPage);
      }
      setCurrentIndex(restoredIndex);
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [setCurrentIndex, setPageState]);

  const setAnswer = useCallback(
    (questionId: string, answer: AnswerValue) => {
      const isEmpty = isEmptyAnswer(answer);

      setAnswersState((prevAnswers) => {
        // Find index of the question being answered
        const questionIndex = questions.findIndex(
          (q) => q.questionId === questionId,
        );

        // Only clear subsequent answers if answer actually changed
        const previousAnswer = prevAnswers[questionId];
        const answerChanged =
          JSON.stringify(previousAnswer) !== JSON.stringify(answer);

        // Check if there are any answers after this question
        const hasAnswersAfter =
          questionIndex >= 0 &&
          questions
            .slice(questionIndex + 1)
            .some((q) => prevAnswers[q.questionId] !== undefined);

        if (answerChanged && hasAnswersAfter) {
          // Clear all answers after this question
          const newAnswers: Record<string, AnswerValue> = {};
          questions.slice(0, questionIndex + 1).forEach((q) => {
            if (prevAnswers[q.questionId] !== undefined) {
              newAnswers[q.questionId] = prevAnswers[q.questionId];
            }
          });
          newAnswers[questionId] = answer;
          if (isEmpty) delete newAnswers[questionId];
          sessionStorageExpiring.set(sessionItems.QUIZ_ANSWERS, newAnswers);
          return newAnswers;
        }

        const newAnswers = { ...prevAnswers, [questionId]: answer };
        if (isEmpty) delete newAnswers[questionId];
        sessionStorageExpiring.set(sessionItems.QUIZ_ANSWERS, newAnswers);
        return newAnswers;
      });
    },
    [questions],
  );

  const onValidationChange = useCallback(
    (questionId: string, isValid: boolean) => {
      setValidationState((prev) => ({
        ...prev,
        [questionId]: isValid,
      }));
    },
    [],
  );

  const goNext = useCallback(async (): Promise<boolean> => {
    const latestAnswers = answersRef.current;
    const currentQuestion = questions[currentIndex];
    const pageVariant =
      PAGE_VARIANT[
        latestAnswers["sex-at-birth"] === "female"
          ? Variant.FEMALE
          : Variant.DEFAULT
      ];

    const isEmailCurrentQuestion =
      currentQuestion?.questionType === QuestionType.TEXT &&
      currentQuestion.answers.some((a) => a.inputType === TextInputType.EMAIL);

    // Fire started event once when both name and email are collected
    if (
      isEmailCurrentQuestion &&
      !startedFiredRef.current &&
      latestAnswers["user-name"] &&
      latestAnswers["email"]
    ) {
      startedFiredRef.current = true;
      setIsQuizStarted(true);
      fireQuizStarted(
        pageVariant,
        formatExperimentVariants(experimentAssignments),
      );
      quizActiveRef.current = true;
      void fireStartedEvent(
        api,
        sessionId,
        latestAnswers,
        experimentAssignments,
      ).catch((error) => {
        if (isCaptchaVerificationRequiredError(error)) {
          if (process.env.NODE_ENV !== "production") {
            console.warn(
              "Started event rejected due to missing/invalid captcha verification; terminating session",
              { sessionId },
            );
          }
          terminateSessionWithError();
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to post started event:", error);
        }
      });
    }

    // Fire answer event before navigating away
    if (currentQuestion) {
      const currentAnswer = latestAnswers[currentQuestion.questionId];
      if (currentAnswer !== undefined) {
        fireAnswerEvent(
          currentQuestion,
          currentAnswer,
          visibleQuestionsRef.current,
          pageVariant,
          t,
        );
      }
    }

    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length) {
      const nextQuestion = questions[nextIndex];
      if (
        !HIDDEN_QUESTION_TYPES.includes(nextQuestion.questionType) &&
        isConditionMet(nextQuestion.conditionalOn, latestAnswers)
      ) {
        setCurrentIndex(nextIndex);
        window.history.pushState(
          {
            sapQuestionIndex: nextIndex,
            sapPageState: PageState.QUESTIONNAIRE,
          },
          "",
        );
        return true;
      }
      nextIndex++;
    }

    // No more visible questions — fire finish event then transition to polling
    quizActiveRef.current = false;
    fireQuizComplete(pageVariant);
    setPageState(PageState.POLLING);
    window.history.pushState(
      { sapQuestionIndex: currentIndex, sapPageState: PageState.POLLING },
      "",
    );
    if (onComplete) {
      onComplete(latestAnswers);
    }
    return true;
  }, [
    currentIndex,
    questions,
    onComplete,
    api,
    sessionId,
    experimentAssignments,
    terminateSessionWithError,
    setCurrentIndex,
    setPageState,
    t,
  ]);

  /** Visible questions are the questions that a user will see based on their current answers
   * If there is a condition that relies on an answer they haven't given yet,
   * assume that they will see it so the progress bar doesn't move backwards when they click next
   */
  const visibleQuestions = useMemo(
    () =>
      questions.filter(
        (q) =>
          !HIDDEN_QUESTION_TYPES.includes(q.questionType) &&
          isConditionMet(q.conditionalOn, answers),
      ),
    [questions, answers],
  );
  visibleQuestionsRef.current = visibleQuestions;
  questionsRef.current = questions;

  /**
   * goBack will find the index within the visible questions, then move one back in the visible questions
   * not the full question list so we don't need to check for conditions again.
   * Visible questions will only change when moving forward
   */
  const goBack = useCallback(() => {
    const currentQuestion = questions[currentIndex];
    const visibleIndex = visibleQuestions.findIndex(
      (q) => q.questionId === currentQuestion?.questionId,
    );
    if (visibleIndex > 0) {
      const pageVariant =
        PAGE_VARIANT[
          answersRef.current["sex-at-birth"] === "female"
            ? Variant.FEMALE
            : Variant.DEFAULT
        ];
      fireBackEvent(currentQuestion, visibleQuestions, pageVariant);
    }
    window.history.back();
  }, [currentIndex, questions, visibleQuestions]);

  const goToQuestion = useCallback(
    (questionId: string) => {
      const targetIndex = questions.findIndex(
        (question) => question.questionId === questionId,
      );
      if (targetIndex === -1) return;

      setCurrentIndex(targetIndex);
      window.history.pushState(
        {
          sapQuestionIndex: targetIndex,
          sapPageState: PageState.QUESTIONNAIRE,
        },
        "",
      );
    },
    [questions, setCurrentIndex],
  );

  const completeAssessment = useCallback(
    (result: AssessmentResult, newInferenceId: string) => {
      setAssessmentResult(result);
      setInferenceId(newInferenceId);
      sessionStorageExpiring.set(sessionItems.ASSESSMENT_RESULT, result);
      sessionStorageExpiring.set(sessionItems.INFERENCE_ID, newInferenceId);
      setPageState(PageState.RESULTS);
      window.history.replaceState(
        { sapQuestionIndex: currentIndex, sapPageState: PageState.RESULTS },
        "",
      );
    },
    [setPageState, currentIndex],
  );

  const currentQuestion = questions[currentIndex] || null;
  const currentAnswer = currentQuestion
    ? answers[currentQuestion.questionId]
    : null;
  // Email questions require explicit NeverBounce approval (validationState must be true).
  // All other questions: undefined means "no validation needed" = valid.
  const isEmailCurrentQuestion =
    currentQuestion?.questionType === QuestionType.TEXT &&
    currentQuestion.answers.some((a) => a.inputType === TextInputType.EMAIL);
  const isCurrentFieldValid = currentQuestion
    ? isEmailCurrentQuestion
      ? validationState[currentQuestion.questionId] === true
      : validationState[currentQuestion.questionId] !== false
    : true;
  const canGoNext =
    pageState === PageState.QUESTIONNAIRE &&
    isAnswerValid(currentAnswer, currentQuestion) &&
    isCurrentFieldValid;
  const canGoBack =
    pageState === PageState.QUESTIONNAIRE && currentIndex > 0;

  const state = {
    questions,
    answers: Object.entries(answers).map(([questionId, answerId]) => ({
      questionId,
      answerId,
    })),
    currentIndex,
  };

  const isLastQuestion = useMemo(() => {
    for (let i = currentIndex + 1; i < questions.length; i++) {
      if (isConditionMet(questions[i].conditionalOn, answers)) return false;
    }
    return true;
  }, [questions, currentIndex, answers]);

  const value: IAssessmentContext = {
    state,
    questions,
    visibleQuestions,
    currentIndex,
    answers: state.answers,
    answersRecord: answers,
    currentQuestion,
    currentAnswer,
    canGoNext,
    canGoBack,
    isLastQuestion,
    isQuizStarted,
    assessmentResult,
    inferenceId,
    pageState,
    experimentAssignments,
    completeAssessment,
    goNext,
    goBack,
    goToQuestion,
    setAnswer,
    isLoading,
    error,
    onValidationChange,
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment(): IAssessmentContext {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
}
